import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import {
  CalendarClock, Plus, X, Send, Save, Eye, Trash2, Pencil,
  CheckCircle2, AlertCircle, Clock, Users, FileSpreadsheet,
  History, Mail, Loader2, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import FirstTimeHint from '../../components/FirstTimeHint';
import { useNavigate } from 'react-router-dom';

const DIAS_SEMANA = [
  { value: 0, label: 'Lunes' },
  { value: 1, label: 'Martes' },
  { value: 2, label: 'Miércoles' },
  { value: 3, label: 'Jueves' },
  { value: 4, label: 'Viernes' },
  { value: 5, label: 'Sábado' },
  { value: 6, label: 'Domingo' },
];

const labelDia = (d) => DIAS_SEMANA.find((x) => x.value === Number(d))?.label || '-';

/** Lista editable de chips de email — agregar/quitar. */
function EmailListInput({ label, items, onChange, placeholder, testId = 'emails' }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim().toLowerCase();
    if (!v || items.includes(v)) { setDraft(''); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error('Email inválido');
      return;
    }
    onChange([...items, v]);
    setDraft('');
  };
  const remove = (e) => onChange(items.filter((x) => x !== e));
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
        <Users className="w-3.5 h-3.5" /> {label}
      </label>
      <div className="flex gap-1 mb-1.5">
        <Input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="h-8 text-xs"
          data-testid={`${testId}-input`}
        />
        <Button type="button" size="sm" onClick={add} className="h-8" data-testid={`${testId}-add`}>
          <Plus className="w-3 h-3" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1">
        {items.length === 0 ? (
          <p className="text-[11px] text-muted-foreground italic">Sin destinatarios</p>
        ) : items.map((e) => (
          <Badge key={e} variant="secondary" className="font-normal text-[11px] gap-1" data-testid={`${testId}-chip-${e}`}>
            <Mail className="w-3 h-3" /> {e}
            <button onClick={() => remove(e)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  if (!status) return <Badge variant="outline" className="text-[10px]">Nunca enviado</Badge>;
  if (status === 'success') {
    return <Badge className="bg-emerald-500 hover:bg-emerald-600 text-[10px]"><CheckCircle2 className="w-3 h-3 mr-1" />OK</Badge>;
  }
  return <Badge className="bg-red-500 hover:bg-red-600 text-[10px]"><AlertCircle className="w-3 h-3 mr-1" />Error</Badge>;
}

const fmtFechaCorta = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const EMPTY_FORM = {
  id: '',
  nombre: '',
  tipo: '',
  enabled: true,
  frecuencia: 'weekly',
  dia_semana: 0,
  hora: '08:00',
  to: [],
  cc: [],
  filtros: {},
};

export default function ReportesProgramadosPage() {
  const navigate = useNavigate();
  const [tipos, setTipos] = useState([]);
  const [reportes, setReportes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {...form} or null
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [logOpen, setLogOpen] = useState(null); // reporte_id
  const [logItems, setLogItems] = useState([]);
  const [sendingId, setSendingId] = useState(null);

  const loadTipos = useCallback(async () => {
    try {
      const res = await api.get('/reportes-programados/tipos');
      setTipos(res.data.tipos || []);
    } catch {
      toast.error('Error cargando tipos de reportes');
    }
  }, []);

  const loadReportes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/reportes-programados');
      setReportes(res.data.items || []);
    } catch {
      toast.error('Error cargando reportes programados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTipos();
    loadReportes();
  }, [loadTipos, loadReportes]);

  const openNew = () => {
    setEditing({ ...EMPTY_FORM });
  };
  const openEdit = (rep) => {
    setEditing({ ...EMPTY_FORM, ...rep, filtros: rep.filtros || {} });
  };

  const onTipoChange = (tipo) => {
    if (!editing) return;
    const spec = tipos.find((t) => t.tipo === tipo);
    setEditing((prev) => ({
      ...prev,
      tipo,
      filtros: spec?.filtros_default || {},
    }));
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.nombre.trim()) { toast.error('Falta nombre'); return; }
    if (!editing.tipo) { toast.error('Falta seleccionar tipo de reporte'); return; }
    if (!editing.to?.length) { toast.error('Agrega al menos un destinatario'); return; }
    setSaving(true);
    try {
      const payload = {
        nombre: editing.nombre.trim(),
        tipo: editing.tipo,
        enabled: !!editing.enabled,
        frecuencia: editing.frecuencia,
        dia_semana: Number(editing.dia_semana || 0),
        hora: editing.hora,
        to: editing.to,
        cc: editing.cc,
        filtros: editing.filtros || {},
      };
      if (editing.id) {
        await api.put(`/reportes-programados/${editing.id}`, payload);
        toast.success('Reporte actualizado');
      } else {
        await api.post('/reportes-programados', payload);
        toast.success('Reporte creado');
      }
      setEditing(null);
      await loadReportes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rep) => {
    if (!window.confirm(`¿Eliminar el reporte programado "${rep.nombre}"?`)) return;
    try {
      await api.delete(`/reportes-programados/${rep.id}`);
      toast.success('Reporte eliminado');
      await loadReportes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleToggleEnabled = async (rep) => {
    try {
      await api.put(`/reportes-programados/${rep.id}`, { enabled: !rep.enabled });
      toast.success(rep.enabled ? 'Reporte desactivado' : 'Reporte activado');
      await loadReportes();
    } catch {
      toast.error('Error al cambiar estado');
    }
  };

  const handleSendNow = async (rep) => {
    if (!rep.to?.length) { toast.error('Configura destinatarios primero'); return; }
    setSendingId(rep.id);
    try {
      const res = await api.post(`/reportes-programados/${rep.id}/send-now`);
      const d = res.data;
      if (d.sent) {
        toast.success(`Enviado a ${d.to.length} destinatarios (${d.count} filas)`);
      } else if (d.reason === 'no_items') {
        toast.info('Sin datos para enviar');
      } else {
        toast.error(d.error || `No enviado: ${d.reason}`);
      }
      await loadReportes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al enviar');
    } finally {
      setSendingId(null);
    }
  };

  const handlePreview = async (rep) => {
    setPreviewOpen(true);
    setPreview(null);
    setPreviewLoading(true);
    try {
      const res = await api.get(`/reportes-programados/${rep.id}/preview`);
      setPreview({ ...res.data, _rep: rep });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al generar vista previa');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleOpenLog = async (rep) => {
    setLogOpen(rep);
    setLogItems([]);
    try {
      const res = await api.get(`/reportes-programados/${rep.id}/log?limit=30`);
      setLogItems(res.data.items || []);
    } catch {
      toast.error('Error cargando historial');
    }
  };

  const tipoLabel = (t) => tipos.find((x) => x.tipo === t)?.label || t;
  const filtrosSchema = editing ? (tipos.find((t) => t.tipo === editing.tipo)?.filtros_schema || []) : [];

  return (
    <div className="space-y-4 p-2 md:p-4" data-testid="reportes-programados-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CalendarClock className="w-6 h-6 text-blue-600" /> Reportes Programados
          </h1>
          <p className="text-sm text-muted-foreground">
            Envía automáticamente reportes Excel por correo cada día/semana.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/configuracion/reportes-programados/analytics')} data-testid="btn-analytics">
            <BarChart3 className="w-4 h-4 mr-2" /> Métricas
          </Button>
          <Button onClick={openNew} data-testid="btn-new-reporte">
            <Plus className="w-4 h-4 mr-2" /> Nuevo reporte programado
          </Button>
        </div>
      </div>

      <FirstTimeHint
        hintId="reportes-programados-intro"
        title="Tip: tu primer reporte programado en 1 minuto"
        description="Haz clic en 'Nuevo reporte programado'. Elige el tipo (ej. CxC pendientes), agenda la hora (ej. Lunes 08:00), agrega correos en TO (presiona Enter para añadir) y guarda. El Excel se enviará automáticamente cada semana sin que tengas que entrar al ERP."
      />

      {/* Lista */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            {loading ? 'Cargando...' : `${reportes.length} reportes`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : reportes.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <Mail className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Aún no hay reportes programados.</p>
              <p className="text-xs mt-1">Haz clic en "Nuevo reporte programado" para crear el primero.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="reportes-table">
                <thead>
                  <tr className="border-b bg-muted/50 text-xs text-muted-foreground">
                    <th className="text-left px-3 py-2 font-medium">Nombre</th>
                    <th className="text-left px-3 py-2 font-medium">Tipo</th>
                    <th className="text-left px-3 py-2 font-medium">Frecuencia</th>
                    <th className="text-left px-3 py-2 font-medium">Hora</th>
                    <th className="text-left px-3 py-2 font-medium">Destinatarios</th>
                    <th className="text-left px-3 py-2 font-medium">Último envío</th>
                    <th className="text-center px-3 py-2 font-medium">Activo</th>
                    <th className="text-right px-3 py-2 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {reportes.map((rep) => (
                    <tr key={rep.id} className="border-b hover:bg-muted/30" data-testid={`reporte-row-${rep.id}`}>
                      <td className="px-3 py-2.5 font-medium">{rep.nombre}</td>
                      <td className="px-3 py-2.5 text-xs">{tipoLabel(rep.tipo)}</td>
                      <td className="px-3 py-2.5 text-xs">
                        {rep.frecuencia === 'daily' ? 'Diario' : `Semanal · ${labelDia(rep.dia_semana)}`}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs">{rep.hora}</td>
                      <td className="px-3 py-2.5 text-xs">
                        <Badge variant="outline" className="text-[10px]">{rep.to?.length || 0} TO</Badge>
                        {rep.cc?.length > 0 && (
                          <Badge variant="outline" className="text-[10px] ml-1">{rep.cc.length} CC</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-mono">{fmtFechaCorta(rep.ultima_ejecucion)}</span>
                          <div className="flex items-center gap-1">
                            <StatusBadge status={rep.ultimo_status} />
                            {rep.ultima_count > 0 && <span className="text-[10px] text-muted-foreground">({rep.ultima_count})</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <Switch
                          checked={!!rep.enabled}
                          onCheckedChange={() => handleToggleEnabled(rep)}
                          data-testid={`toggle-${rep.id}`}
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Vista previa"
                            onClick={() => handlePreview(rep)} data-testid={`btn-preview-${rep.id}`}>
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600" title="Enviar ahora"
                            onClick={() => handleSendNow(rep)} disabled={sendingId === rep.id} data-testid={`btn-send-${rep.id}`}>
                            {sendingId === rep.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Historial de envíos"
                            onClick={() => handleOpenLog(rep)} data-testid={`btn-log-${rep.id}`}>
                            <History className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Editar"
                            onClick={() => openEdit(rep)} data-testid={`btn-edit-${rep.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" title="Eliminar"
                            onClick={() => handleDelete(rep)} data-testid={`btn-delete-${rep.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Modal Editor ─── */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="editor-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5" />
              {editing?.id ? 'Editar reporte programado' : 'Nuevo reporte programado'}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <Tabs defaultValue="basico" className="space-y-3">
              <TabsList>
                <TabsTrigger value="basico" data-testid="tab-basico">Básico</TabsTrigger>
                <TabsTrigger value="schedule" data-testid="tab-schedule">Programación</TabsTrigger>
                <TabsTrigger value="destinatarios" data-testid="tab-destinatarios">Destinatarios</TabsTrigger>
                {filtrosSchema.length > 0 && (
                  <TabsTrigger value="filtros" data-testid="tab-filtros">Filtros</TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="basico" className="space-y-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Nombre del reporte *</label>
                  <Input
                    value={editing.nombre}
                    onChange={(e) => setEditing({ ...editing, nombre: e.target.value })}
                    placeholder="Ej. OCs aprobadas semanal Lunes"
                    data-testid="input-nombre"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Tipo de reporte *</label>
                  <Select value={editing.tipo} onValueChange={onTipoChange}>
                    <SelectTrigger data-testid="select-tipo">
                      <SelectValue placeholder="Selecciona el tipo de reporte" />
                    </SelectTrigger>
                    <SelectContent>
                      {tipos.map((t) => (
                        <SelectItem key={t.tipo} value={t.tipo}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editing.tipo && (
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {tipos.find((x) => x.tipo === editing.tipo)?.descripcion}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Switch
                    checked={!!editing.enabled}
                    onCheckedChange={(v) => setEditing({ ...editing, enabled: v })}
                    data-testid="switch-enabled"
                  />
                  <span className="text-sm">Activo (se ejecutará automáticamente)</span>
                </div>
              </TabsContent>

              <TabsContent value="schedule" className="space-y-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Frecuencia</label>
                  <Select value={editing.frecuencia} onValueChange={(v) => setEditing({ ...editing, frecuencia: v })}>
                    <SelectTrigger data-testid="select-frecuencia">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Semanal</SelectItem>
                      <SelectItem value="daily">Diario</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editing.frecuencia === 'weekly' && (
                  <div>
                    <label className="block text-xs font-medium mb-1">Día de la semana</label>
                    <Select value={String(editing.dia_semana)} onValueChange={(v) => setEditing({ ...editing, dia_semana: Number(v) })}>
                      <SelectTrigger data-testid="select-dia-semana">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DIAS_SEMANA.map((d) => (
                          <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium mb-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Hora (24h)
                  </label>
                  <Input
                    type="time"
                    value={editing.hora}
                    onChange={(e) => setEditing({ ...editing, hora: e.target.value })}
                    data-testid="input-hora"
                  />
                </div>
              </TabsContent>

              <TabsContent value="destinatarios" className="space-y-3">
                <EmailListInput
                  label="Para (TO) *"
                  items={editing.to}
                  onChange={(items) => setEditing({ ...editing, to: items })}
                  placeholder="ejemplo@chirino.cl"
                  testId="emails-to"
                />
                <EmailListInput
                  label="Copia (CC)"
                  items={editing.cc}
                  onChange={(items) => setEditing({ ...editing, cc: items })}
                  placeholder="ejemplo@chirino.cl"
                  testId="emails-cc"
                />
              </TabsContent>

              {filtrosSchema.length > 0 && (
                <TabsContent value="filtros" className="space-y-3">
                  {filtrosSchema.map((f) => (
                    <div key={f.key}>
                      <label className="block text-xs font-medium mb-1">{f.label}</label>
                      <Input
                        type={f.type === 'number' ? 'number' : 'text'}
                        value={editing.filtros[f.key] ?? f.default ?? ''}
                        min={f.min}
                        max={f.max}
                        onChange={(e) => setEditing({
                          ...editing,
                          filtros: { ...editing.filtros, [f.key]: f.type === 'number' ? Number(e.target.value) : e.target.value },
                        })}
                        data-testid={`filtro-${f.key}`}
                      />
                    </div>
                  ))}
                </TabsContent>
              )}
            </Tabs>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="btn-cancel-editor">
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="btn-save-editor">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              {editing?.id ? 'Actualizar' : 'Crear reporte'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Modal Preview ─── */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" /> Vista previa: {preview?._rep?.nombre || ''}
            </DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : preview ? (
            <div className="space-y-3">
              <div>
                <h3 className="text-base font-semibold">{preview.title}</h3>
                {preview.subtitle && <p className="text-xs text-muted-foreground">{preview.subtitle}</p>}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {preview.summary?.map((s, i) => (
                  <div key={i} className="bg-muted/40 border rounded p-2">
                    <p className="text-[10px] text-muted-foreground uppercase">{s.label}</p>
                    <p className="text-sm font-bold">{typeof s.value === 'number' ? s.value.toLocaleString('es-CL') : s.value}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Mostrando primeras {preview.rows?.length || 0} filas de {preview.total_rows}.
                {preview.total_rows > 50 && ' (Truncado, el reporte completo se enviará por email)'}
              </p>
              <div className="overflow-x-auto border rounded">
                <table className="w-full text-xs" data-testid="preview-table">
                  <thead className="bg-muted">
                    <tr>{preview.columns?.map((c) => <th key={c.key} className="px-2 py-1.5 text-left font-medium">{c.label}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.rows?.map((r, i) => (
                      <tr key={i} className="border-t">
                        {preview.columns.map((c) => (
                          <td key={c.key} className="px-2 py-1 text-[11px]">
                            {c.fmt === 'money' && typeof r[c.key] === 'number'
                              ? `$${Math.round(r[c.key]).toLocaleString('es-CL')}`
                              : (r[c.key] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* ─── Modal Log ─── */}
      <Dialog open={!!logOpen} onOpenChange={(o) => !o && setLogOpen(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="log-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" /> Historial: {logOpen?.nombre}
            </DialogTitle>
          </DialogHeader>
          {logItems.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Sin envíos registrados aún.</p>
          ) : (
            <div className="space-y-1 max-h-[60vh] overflow-y-auto">
              {logItems.map((l, i) => (
                <div key={i} className="flex items-center gap-2 text-xs border-b py-1.5" data-testid={`log-item-${i}`}>
                  <span className="font-mono text-muted-foreground w-32">{fmtFechaCorta(l.timestamp)}</span>
                  {l.success ? (
                    <Badge className="bg-emerald-500 text-[10px]">OK</Badge>
                  ) : l.skipped ? (
                    <Badge variant="outline" className="text-[10px]">Omitido</Badge>
                  ) : (
                    <Badge className="bg-red-500 text-[10px]">Error</Badge>
                  )}
                  <span>{l.manual ? 'Manual' : 'Automático'}</span>
                  <span className="text-muted-foreground">·</span>
                  <span>{l.count ?? 0} filas</span>
                  {l.error && <span className="text-red-600 truncate max-w-md" title={l.error}>{l.error}</span>}
                  {l.skipped && <span className="text-amber-700">{l.skipped}</span>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
